import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import NavBar from './nav_bar.js';
import BackButton from './BackButton.js';
import usePageSeo, { buildEventJsonLd } from './seo.js';
import useBfcacheReset from './bfcache.js';
import SupportLine from './support_line.js';
import './index.css';

// /events — a single flier image. Tapping it goes to /tickets (the all-in-one
// ticket page). "Buy general admission" skips straight to Stripe Checkout for
// the no-artist /tickets/general ticket. The flier is a placeholder until real
// art lands in public/events/ (or EVENT_FLIER_URL is set on the backend).
function EventsPage() {
  const navigate = useNavigate();
  const [flier, setFlier] = useState('/events/flier.svg');
  const [eventName, setEventName] = useState(null);
  const [date, setDate] = useState(null);
  const [dateISO, setDateISO] = useState(null);
  const [address, setAddress] = useState(null);
  const [artists, setArtists] = useState([]);
  const [configured, setConfigured] = useState(true);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/events/lineup');
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled) return;
        if (data.flier) setFlier(data.flier);
        if (data.eventName) setEventName(data.eventName);
        if (data.date) setDate(data.date);
        if (data.dateISO) setDateISO(data.dateISO);
        if (data.address) setAddress(data.address);
        if (Array.isArray(data.artists)) setArtists(data.artists);
        setConfigured(data.ticketingConfigured !== false);
      } catch {
        /* keep the default placeholder */
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const jsonLd = useMemo(
    () => (eventName
      ? buildEventJsonLd({
          name: eventName,
          dateISO,
          address,
          artists,
          ticketUrl: `${window.location.origin}/tickets`,
        })
      : null),
    [eventName, dateISO, address, artists],
  );
  usePageSeo({
    title: eventName ? `${eventName} — tickets & lineup | MP3JUUG` : 'Events | MP3JUUG',
    description: eventName
      ? `${eventName}${date ? ` — ${date}` : ''}${address ? ` at ${address}` : ''}. Tickets, lineup, and song requests.`
      : 'Live events from MP3JUUG.',
    jsonLd,
  });

  // Returning here via Back after the Stripe redirect restores the frozen
  // page — clear the stuck "Opening checkout…" button state.
  useBfcacheReset(() => setStarting(false), starting);

  const buyGeneral = useCallback(async () => {
    setStarting(true);
    setError('');
    try {
      const res = await fetch('/tickets/general/checkout', { method: 'POST' });
      const data = await res.json().catch(() => null);
      if (!res.ok || !data?.url) throw new Error(data?.msg || 'Could not start checkout.');
      window.location.href = data.url;
    } catch (err) {
      setError(err.message);
      setStarting(false);
    }
  }, []);

  return (
    <>
      <NavBar />
      <main className='page'>
        <div className='page-content'>
          <BackButton fallback='/' />
          <h1 className='event-name'>{eventName || 'Events'}</h1>
          {date && <p className='event-date'>{date}</p>}
          {address && <p className='event-address'>{address}</p>}
          <button
            type='button'
            className='ticket-poster-btn'
            onClick={() => navigate('/tickets')}
            aria-label='See the full lineup'
          >
            <img className='ticket-poster' src={flier} alt='Event flier' />
            <span className='ticket-poster-cta'>See lineup</span>
          </button>
          <button
            type='button'
            className='btn btn-primary'
            onClick={buyGeneral}
            disabled={starting || configured === false}
          >
            {configured === false
              ? 'Tickets not on sale yet'
              : starting ? 'Opening checkout…' : 'Buy general admission'}
          </button>
          {error && <p className='error-text'>{error}</p>}
          <SupportLine />
        </div>
      </main>
    </>
  );
}

export default EventsPage;
